import { FC, useState } from 'react';
import { ChevronRight, TriangleAlert, Zap } from 'lucide-react';
import StudentsCloserLookModal from './StudentsCloserLookModal';
import { Button } from 'primereact/button';
import {
  ClassDiagnosticStatusReport,
  StudentReport,
  TOO_FAST_SECONDS,
  DiagnosticStatus,
  DismissedStudent,
  getStudentDiagnosticStatus,
  isStudentDismissed,
  buildDismissedStudent,
} from './helper/chartHelper';
import { CRITICAL_SECONDS, getStudentLastName, TOO_LONG_SECONDS } from '../../CloserLook/closerLookHelper';

interface Props {
  diagnosticResults: ClassDiagnosticStatusReport;
}

const categorizeStudent = (report: StudentReport): 'too-long' | 'too-fast' | 'none' => {
  const { totalActiveTime } = report;

  // A missing measurement is "unknown", not "0 minutes" — don't classify it.
  if (totalActiveTime == null) return 'none';

  // Derive the effective status so a reset diagnostic (retakeAssigned) is treated as
  // NOT_STARTED and no longer flagged as "taking too long" / "finished quickly".
  const status = getStudentDiagnosticStatus(report);

  if (status === DiagnosticStatus.IN_PROGRESS && totalActiveTime >= TOO_LONG_SECONDS) return 'too-long';
  if (status === DiagnosticStatus.COMPLETED && totalActiveTime < TOO_FAST_SECONDS) return 'too-fast';

  return 'none';
};

const StudentsCloserLookCard: FC<Props> = ({ diagnosticResults }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [initialTab, setInitialTab] = useState<0 | 1>(0);

  const [dismissedStudents, setDismissedStudents] = useState<DismissedStudent[]>(() =>
    JSON.parse(localStorage.getItem('dismissedStudents') || '[]'),
  );

  const handleDismiss = (report: StudentReport) => {
    setDismissedStudents(prev => {
      const next = [...prev, buildDismissedStudent(report)];
      localStorage.setItem('dismissedStudents', JSON.stringify(next));
      return next;
    });
  };

  // "Taking too long" list ordered alphabetically by last name.
  const tooLongStudents = [...diagnosticResults.filter(el => categorizeStudent(el) === 'too-long')].sort((a, b) =>
    getStudentLastName(a.student).localeCompare(getStudentLastName(b.student)),
  );

  // "Finished quickly" list ordered alphabetically by last name. Dismissed students stay in the
  // list but are pushed to the end (and rendered without action buttons, see CloserLookStudentRow).
  const tooFastStudents = [...diagnosticResults.filter(el => categorizeStudent(el) === 'too-fast')].sort((a, b) =>
    getStudentLastName(a.student).localeCompare(getStudentLastName(b.student)),
  );
  const orderedTooFastStudents = [
    ...tooFastStudents.filter(student => !isStudentDismissed(student, dismissedStudents)),
    ...tooFastStudents.filter(student => isStudentDismissed(student, dismissedStudents)),
  ];
  // Dismissed students remain visible but must not count toward the card/tab/record totals.
  const tooFastCount = tooFastStudents.filter(student => !isStudentDismissed(student, dismissedStudents)).length;

  const criticalCount = tooLongStudents.filter(el => (el.totalActiveTime ?? 0) > CRITICAL_SECONDS).length;
  const mildCount = tooLongStudents.length - criticalCount;

  const showTooLong = tooLongStudents.length > 0;
  const showTooFast = tooFastCount > 0;

  if (!showTooLong && !showTooFast) return null;

  const openModal = (tab: 0 | 1) => {
    setInitialTab(tab);
    setModalVisible(true);
  };

  return (
    <>
      <span className="lg:tw-w-[2px] tw-w-[268px] lg:tw-h-[350px] tw-h-[2px] tw-bg-indigo-200 tw-self-center"></span>
      <div className="tw-flex tw-p-[2px] tw-self-center">
        <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-flex tw-flex-col tw-gap-3 tw-min-w-[230px]">
          <h3 className="tw-text-base tw-font-bold tw-font-atkinson tw-text-neutral-950 tw-m-0 tw-leading-[20px]">
            Students who need a closer look
          </h3>

          {showTooLong && (
            <div
              className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-2 tw-p-4 tw-rounded-xl tw-border-solid tw-border-red-100 tw-border-b-[8px] hover:tw-border-red-400 tw-cursor-pointer"
              onClick={() => openModal(0)}
            >
              <div className="tw-flex tw-flex-col tw-gap-1">
                <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                  <span className="tw-text-4xl tw-font-bold tw-font-atkinson tw-text-red-700 tw-leading-none">
                    {tooLongStudents.length}
                  </span>
                  <span className="tw-text-base tw-font-atkinson tw-text-neutral-950 tw-font-semibold">
                    Taking too long
                  </span>
                </div>
                <div className="tw-flex tw-flex-row tw-flex-wrap tw-gap-1">
                  {criticalCount > 0 && (
                    <span className="tw-flex tw-items-center tw-font-bold tw-gap-1 tw-bg-red-100 tw-text-red-700 tw-text-xs tw-font-atkinson tw-rounded-lg tw-px-2 tw-py-1">
                      <TriangleAlert size={14} className="tw-font-bold" />
                      {criticalCount} critical: &gt;90min
                    </span>
                  )}
                  {mildCount > 0 && (
                    <span className="tw-flex tw-items-center tw-font-bold tw-text-red-600 tw-text-xs tw-font-atkinson tw-px-2 tw-py-1 tw-rounded-lg tw-border-solid tw-border-[2px] tw-border-red-100">
                      {mildCount} mild: 70–90min
                    </span>
                  )}
                </div>
              </div>
              <Button
                className="!tw-p-1"
                aria-label="Taking too long"
                aria-labelledby="Taking too long"
                link
                icon={<ChevronRight size={24} className="tw-text-blue-500 hover:tw-text-white tw-mt-1 tw-shrink-0" />}
              />
            </div>
          )}

          {showTooFast && (
            <div
              className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-2 tw-p-4 tw-rounded-xl tw-border-solid tw-border-amber-100 tw-border-b-[8px] hover:tw-border-amber-400 tw-cursor-pointer"
              onClick={() => openModal(1)}
            >
              <div className="tw-flex tw-flex-col tw-gap-1">
                <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                  <span className="tw-text-4xl tw-font-bold tw-font-atkinson tw-text-amber-500 tw-leading-none">
                    {tooFastCount}
                  </span>
                  <span className="tw-text-base tw-font-atkinson tw-text-neutral-950 tw-font-semibold">
                    Finished quickly
                  </span>
                </div>
                <div>
                  <span className="tw-flex tw-items-center tw-font-bold tw-gap-1 tw-bg-amber-100 tw-text-amber-800 tw-text-xs tw-font-atkinson tw-rounded-lg tw-px-2 tw-py-1">
                    <Zap size={14} className="tw-text-amber-500" />
                    finished in &lt;15 min
                  </span>
                </div>
              </div>
              <Button
                className="!tw-p-1"
                aria-label="Finished quickly"
                aria-labelledby="Finished quickly"
                link
                icon={<ChevronRight size={24} className="tw-text-blue-500 hover:tw-text-white tw-mt-1 tw-shrink-0" />}
              />
            </div>
          )}
        </div>
      </div>

      <StudentsCloserLookModal
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        tooLongStudents={tooLongStudents}
        tooFastStudents={orderedTooFastStudents}
        tooFastCount={tooFastCount}
        dismissedStudents={dismissedStudents}
        onDismiss={handleDismiss}
        initialTab={initialTab}
      />
    </>
  );
};

export default StudentsCloserLookCard;
